// Shared "Completion policy" + "Practical exercise" select pair used by the
// session editor, the track editor and the program wizard. Options come from
// GET /completion-policies and GET /practices (manage|assign); the value is
// the bound id ('' = none / inherit). A "Manage" link opens the admin
// Practice / Assessments tab.
import { useT } from '../lib/i18n';
import React from 'react';
import { Link } from 'react-router-dom';
import { Label } from '@/features/shared/components/ui/label';
import { useCompletionPolicies, usePractices } from '../api/hooks';

const selectCls = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';

interface Props {
  policyId: string;
  practiceId: string;
  onChange: (patch: { policyId?: string; practiceId?: string }) => void;
  /** Copy for the empty option — "None" (session) or "Same as session" (track). */
  emptyLabel?: string;
  manageHref?: string | null;
  idPrefix?: string;
  disabled?: boolean;
  className?: string;
}

export const PolicyPracticeSelects: React.FC<Props> = ({ policyId, practiceId, onChange, emptyLabel, manageHref, idPrefix = 'pps', disabled, className }) => {
  const t = useT();
  const policies = useCompletionPolicies();
  const practices = usePractices();
  const none = emptyLabel || t('training:binding_none', 'None');
  // Keep an inactive-but-bound option visible so an edit does not silently drop it.
  const policyRows = (policies.data || []).filter((p) => p.active || p.id === policyId);
  const practiceRows = (practices.data || []).filter((p) => p.active || p.id === practiceId);
  return (
    <div className={`grid gap-2 sm:grid-cols-2 ${className || ''}`} data-testid="policy-practice-selects">
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-policy`} className="text-xs">{t('training:completion_policy', 'Completion policy')}</Label>
        <select id={`${idPrefix}-policy`} className={selectCls} value={policyId} disabled={disabled || policies.isLoading} onChange={(e) => onChange({ policyId: e.target.value })}>
          <option value="">{none}</option>
          {policyRows.map((p) => <option key={p.id} value={p.id}>{p.name}{p.active ? '' : ` (${t('training:inactive', 'inactive')})`}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-practice`} className="text-xs">{t('training:practice_title', 'Practical exercise')}</Label>
        <select id={`${idPrefix}-practice`} className={selectCls} value={practiceId} disabled={disabled || practices.isLoading} onChange={(e) => onChange({ practiceId: e.target.value })}>
          <option value="">{none}</option>
          {practiceRows.map((p) => <option key={p.id} value={p.id}>{p.title}{p.active ? '' : ` (${t('training:inactive', 'inactive')})`}</option>)}
        </select>
      </div>
      <p className="sm:col-span-2 text-[11px] text-muted-foreground">
        {t('training:binding_help', 'A track binding overrides the session binding; the effective policy and exercise land on each participant\'s assignment.')}
        {manageHref && <> · <Link to={manageHref} className="text-orange-600 hover:underline">{t('training:manage_policies_link', 'Manage policies & exercises')}</Link></>}
      </p>
    </div>
  );
};

export default PolicyPracticeSelects;
